import { path } from "ramda";
import { type ReactElement } from "react";
import type { Step } from "react-joyride";
import { matchPath } from "react-router";

import ImgIntroduction1 from "../assets/img/introduction/intro_1.png";
import ImgIntroduction2 from "../assets/img/introduction/intro_2.png";
import ImgIntroduction3 from "../assets/img/introduction/intro_3.png";
import ImgIntroduction4 from "../assets/img/introduction/intro_4.png";
import ImgIntroduction5 from "../assets/img/introduction/intro_5.png";
import { type Configuration } from "../graphql.js";
import { getProjectLabel, isLicensed } from "../redux/modules/config.js";
import { DashboardCardType, type IGenericCardConfiguration } from "../redux/modules/dashboard.js";
import { GETTING_STARTED_ELEMENT_ID, LICENSE_MODULE } from "../settings/settings.js";
import { type IDashboardConfig } from "../ui-modules/dashboard/DashboardView.js";

/********** MAPPING FROM ROUTE TO DOCUMENTATION PAGE ***************/

export interface IDocumentationToRoute {
  routes: string[];
  docPage: string;
  label: string;
}

// be cautious, the order is important: first found, first match
const documentationToRouteMap: IDocumentationToRoute[] = [
  { routes: ["/login"], docPage: "Login", label: "Login Help" },
  { routes: ["/user"], docPage: "Account%2bSettings", label: "Account Settings Help" },
  { routes: ["/home"], docPage: "Home%2bPage", label: "Home Page Help" },
  {
    routes: ["/project"],
    docPage: `${getProjectLabel(true, false)}%2bDetails`,
    label: `${getProjectLabel(true)} Page Help`,
  },
  { routes: ["/projects"], docPage: "Projects", label: "Projects Help" },

  { routes: ["/content/organisms"], docPage: "Content%2b-%2bIntegrated%2bGenomes", label: "Integrated Genomes Help" },
  { routes: ["/content/vectors"], docPage: "Content%2b-%2bVectors", label: "Vector Genomes Help" },
  { routes: ["/content/savedItems"], docPage: "Content%2b-%2bSaved%2bItems", label: "Saved Items Help" },
  { routes: ["/content/files"], docPage: "Content%2b-%2bFiles", label: "Files Help" },
  { routes: ["/content/ssaSessions"], docPage: "Content%2b-%2bSessions", label: "Sessions Help" },
  { routes: ["/content/events"], docPage: "Playbook%2bEvents", label: "Playbook Events Help" },

  { routes: ["/admin/jobs"], docPage: "Jobs%2bList", label: "Jobs Help" },
  { routes: ["/admin/roles"], docPage: "Roles%2bWeb%2bInterface", label: "Roles Help" },
  { routes: ["/admin/users"], docPage: "Users%2bList%2bWeb%2binterface", label: "Users Help" },
  { routes: ["/admin/playbooks"], docPage: "Playbook%2bAdministration", label: "Playbooks Administration Help" },
  { routes: ["/admin/system"], docPage: "System", label: "System Help" },
  { routes: ["/admin/projectGroups"], docPage: "User%2bGroups%2bAdministration", label: "User Groups Help" },
  { routes: ["/admin/samples"], docPage: "Sample%2bAdministration", label: "Sample Administration Help" },
  { routes: ["/admin/tools"], docPage: "Administrator%2bTools", label: "Administration Tools Help" },
  { routes: ["/admin"], docPage: "Administrator%2bWeb%2bInterface", label: "Administrator Help" },
  { routes: ["/processor/session/:session"], docPage: "Processor%2bSessions", label: "Processor Help" },
  { routes: ["/processor"], docPage: "Processor%2bSessions%2bList", label: "Processor Help" },

  { routes: ["/playbooks"], docPage: "Playbook%2bExecution", label: "Playbook Execution Help" },
  { routes: ["/playbooks/blast", "/job/:id/input"], docPage: "BLAST%2bSearch", label: "BLAST Input Help" },
  { routes: ["/job/:jobId"], docPage: "Blast%2bResults", label: "BLAST Results Help" },
  {
    routes: ["/visualizer/data", "/:entity/:id/expression"],
    docPage: "Analytical%2bData",
    label: "Analytical Viewer Help",
  },
  { routes: ["/visualizer/genome", "/:entity/:id/genome"], docPage: "Genome%2bView", label: "Genome Viewer Help" },
  { routes: ["/visualizer/synteny", "/:entity/:id/synteny"], docPage: "Synteny%2bView", label: "Synteny Viewer Help" },
  { routes: ["/sample"], docPage: "Sample%2bDetails", label: "Sample Details Help" },
  { routes: ["/:entity/:id/summary"], docPage: "Record%2bDetails%2bDisplay", label: "Summary Display Help" },
  { routes: ["/:entity/:id/:related", "/group/", "/search/"], docPage: "Table%2bDisplay", label: "Table Display Help" },
  { routes: ["/:entity/:id"], docPage: "Record%2bDetails%2bDisplay", label: "Summary Display Help" }, // consider we are on the summary if no other doc page matches the route
  { routes: ["/"], docPage: "Setup%2band%2bUsage", label: "Web Interface Help" },
];

export function getRootDocumentationPage(): string {
  return "Documentation%2bLibrary";
}

export function getDocumentationLink(currentPath: string): IDocumentationToRoute {
  return documentationToRouteMap.find(
    (docToRoute: IDocumentationToRoute) =>
      !!docToRoute.routes.find((route: string) => !!matchPath({ path: route, end: false }, currentPath)),
  );
}

const absoluteUrlRegExp = new RegExp("^(?:[a-z]+:)?//", "i");

export function buildHelpLink(helpLink: string, rootPath: string) {
  if (!helpLink || absoluteUrlRegExp.test(helpLink)) {
    return helpLink;
  } else {
    return `${rootPath}/${helpLink}`;
  }
}

/********** INTRODUCTION MODAL DIALOG ***************/

export interface IIntroductionSlide {
  eventKey: number;
  title: string;
  text: string | ReactElement;
  image: string;
}

export const introductionSlides = (appName: string): IIntroductionSlide[] => [
  {
    eventKey: 1,
    title: `Welcome to Genedata ${appName}`,
    text: `Bring together and securely manage your organization’s public and proprietary genomic content.`,
    image: ImgIntroduction1,
  },
  {
    eventKey: 2,
    title: "Everything and everyone in one place",
    text: `Analyze and collaborate efficiently with your project teams, while leveraging available biological data.`,
    image: ImgIntroduction2,
  },
  {
    eventKey: 3,
    title: "Make the most of the available knowledge",
    text: `Make better decisions and gain deeper understanding of your data using integrated visualization tools.`,
    image: ImgIntroduction3,
  },
  {
    eventKey: 4,
    title: "Go beyond Excel, PDFs, and email",
    text: `Share interactive views and lists with collaborators instead of emails with static images.`,
    image: ImgIntroduction4,
  },
  {
    eventKey: 5,
    title: "Let’s get started",
    text: (
      <span>
        From the Content menu, you can explore the genomes and other information that is available to you.
        <br />
        <br />
        Click ‘Next’ to proceed to the Genomes.
      </span>
    ),
    image: ImgIntroduction5,
  },
];

/********** GETTING STARTED HOTSPOTS ***************/

export type ITip = Step;

export const getHomePageTips = (config: Configuration, dashboard: IDashboardConfig): ITip[] => {
  const tips: ITip[] = [
    {
      title: "Content Menu",
      content:
        "Unsure about what information is available to you in Selector? Use the Content menu to explore and locate content of interest.",
      target: `#${GETTING_STARTED_ELEMENT_ID.CONTENT_BROWSER}`,
    },
  ];

  if (isLicensed(config, LICENSE_MODULE.EXPLORER) || isLicensed(config, LICENSE_MODULE.TRACKER)) {
    tips.push({
      title: "Playbooks",
      content:
        "Playbooks provide step-by-step guidance for frequently used tasks. Administrators can add custom playbooks to this menu.",
      target: `#${GETTING_STARTED_ELEMENT_ID.PLAYBOOKS}`,
    });
  }

  tips.push({
    title: "Customize Dashboard",
    content: (
      <>
        <p>
          Cards can be added or reordered on your personal home screen to give you quick access to your data of
          interest.
        </p>
        <p>In addition, you can customize the contents of some cards according to your needs.</p>
      </>
    ),
    // use getting started ID as tip tour ID here (requires both)
    target: `#${GETTING_STARTED_ELEMENT_ID.CUSTOMIZE_DASHBOARD}`,
  });

  const knownTypes = {};

  dashboard?.cardsLayout.md
    .sort((a, b) => {
      if (a.y < b.y) {
        return -1;
      } else if (a.y > b.y) {
        return 1;
      } else {
        if (a.x < b.x) {
          return -1;
        } else if (a.x > b.x) {
          return 1;
        } else {
          return 0;
        }
      }
    })
    .forEach((m) => {
      const cardType: string = dashboard.cardsConfiguration.find((c: IGenericCardConfiguration) => m.i === c.id).type;

      // make sure every type gets only one tip
      if (knownTypes[cardType]) {
        return;
      } else {
        knownTypes[cardType] = 1;
      }

      switch (cardType) {
        case DashboardCardType.projectsList:
          tips.push({
            title: getProjectLabel(true, true),
            content: (
              <>
                <p>
                  All of your {getProjectLabel(false, true)} are listed here on your home screen.{" "}
                  {getProjectLabel(true, true)} are added to this list when you create new{" "}
                  {getProjectLabel(false, true)} or when other users add you to a {getProjectLabel(false, false)}.
                </p>
                <p>
                  You can organize and collaborate with the content in {config?.application?.name} by linking groups and
                  saved views to your {getProjectLabel(false, true)}.
                </p>
              </>
            ),
            target: "#home-page-projects-title",
            placement: "auto",
          });
          break;
        case DashboardCardType.savedItems:
          tips.push({
            title: "Saved Items",
            content: (
              <p>
                Your home screen provides a searchable list of content that has been saved to your{" "}
                {getProjectLabel(false, true)}. This includes items that have been shared by your{" "}
                {getProjectLabel(false, false)} collaborators.
              </p>
            ),
            target: "#saved-items-title",
            placement: "auto",
          });
          break;
        default:
      }
    });

  return tips;
};

export const getProjectPageTips = (privateWorkspace?: boolean): ITip[] => {
  const defaultHints: ITip[] = [
    {
      title: `${getProjectLabel(true, false)} Activity`,
      content: (
        <p>
          You can browse and search all {getProjectLabel(false, false)} activity here. This includes comments that may
          have been posted by you or your collaborators within this {getProjectLabel(false, false)}.
        </p>
      ),
      target: "#project-activity-panel-title",
    },
    {
      title: `${getProjectLabel(true, false)} Settings`,
      content: (
        <p>
          Additional information about this {getProjectLabel(false, false)}, such as other{" "}
          {getProjectLabel(false, false)} members, can be found here in the settings section. If you are an
          administrator of the {getProjectLabel(false, false)}, you can modify the {getProjectLabel(false, false)} and
          invite additional members.
        </p>
      ),
      target: "#project-settings-menu",
      placement: "right",
    },
  ];

  if (!privateWorkspace) {
    defaultHints.push({
      title: `${getProjectLabel(true, false)} Comments`,
      content: (
        <p>
          Share ideas and content with other {getProjectLabel(false, false)} members here in the comment field. Select
          the attachment button to include direct links to content that has been saved in this{" "}
          {getProjectLabel(false, false)}. Tag specific {getProjectLabel(false, false)} members if you want to send them
          a notification.
        </p>
      ),
      target: "#project-activity-comment-input",
    });
  }

  return defaultHints;
};

export const getGenomeViewerPageTips = (): ITip[] => [
  {
    title: "Current Genome",
    content: (
      <p>
        You are currently looking at data for the genome shown here at the top of the genome viewer. You can view
        information for a different genome by selecting a different genome in this list.
      </p>
    ),
    target: "#genome-viewer-current-genome-input",
    placement: "right",
  },
  {
    title: "Manage Tracks",
    content: <p>Click here to select the data tracks that you want to display in the genome viewer below.</p>,
    target: "#genome-viewer-manage-tracks-button",
  },
  {
    title: "Feature Table",
    content: <p>Select a layout option to display a table of related information next to the genome viewer.</p>,
    target: "#genome-viewer-features-table-toggle",
  },
  {
    title: "Genome Flag",
    content: (
      <>
        <p>
          After selecting a region in the genome viewer, you can click here to annotate the range with a Genome Flag.
        </p>
        <p>These flags are visible to you and {getProjectLabel(false, false)} collaborators.</p>
      </>
    ),
    target: "#genome-viewer-flag-button",
  },
];

export const getDataViewerPageTips = (groupMode: boolean, hasDataset: boolean): ITip[] => [
  {
    title: "Dataset Selection",
    content: (
      <p>
        Browse the available datasets here and select one that you want to use for your analysis. Ask your system
        administrator if new datasets need to be added.
      </p>
    ),
    target: "#data-viewer-dataset-selection",
    disableBeacon: !!groupMode,
    placement: "right",
  },
  {
    title: "Chart Type Selection",
    content: (
      <p>
        You can define the type of the chart here. Some chart types are only available when multiple data series are
        selected.
      </p>
    ),
    target: "#data-viewer-chart-type-selection",
    placement: "right",
  },
  {
    title: "X-Axis settings",
    content: (
      <p>
        You can define up to three levels of hierarchy on the X-axis. Values are averaged depending on the conditions
        that you select here.
      </p>
    ),
    target: "#data-viewer-x-axis-selection",
    placement: "right",
    disableBeacon: !hasDataset,
  },
  {
    title: "Data Series",
    content: <p>Select two or more data series to compare multiple profiles against each other.</p>,
    target: "#data-viewer-data-series-selection",
    placement: "right",
    disableBeacon: !hasDataset,
  },
  {
    title: "Time Series Display",
    content: (
      <p>
        If you have selected time intervals or other numerical X-axis conditions, click here to adapt the scale on the
        X-axis according to the numerical values.
      </p>
    ),
    target: "#data-viewer-time-series-toggle",
  },
];

export const getBlastInputPageTips = (): ITip[] => [
  {
    title: "Sequence Input",
    content: (
      <p>
        You can paste or upload one or multiple sequences here to perform a BLAST search. If you have errors in your
        input, you will be notified before the job is submitted.
      </p>
    ),
    target: "#blast-form-sequence-input",
  },
  {
    title: "BLAST Databases",
    content: (
      <p>
        You must select at least one BLAST database for your search. Select multiple databases to search across a larger
        universe of sequences.
      </p>
    ),
    target: "#blast-form-database-input",
  },
  {
    title: "Advanced Parameters",
    content: (
      <p>
        The default parameters are recommended for most BLAST queries. If you want to fine tune your BLAST search, you
        can open the Advanced Parameters panel.
      </p>
    ),
    target: "#blast-form-advanced-params",
  },
];

export const getSummaryPageTips = (): ITip[] => [
  {
    title: "Summary Page",
    content: (
      <p>
        Every entity in the database has a summary page. Here, you can see all available information about the item, and
        understand how it is related to other biological entities and {getProjectLabel(false, true)} in the system.
      </p>
    ),
    target: "#summary-page-title",
  },
];

export const getTablePageTips = (): ITip[] => [
  {
    title: "Table Column tools",
    content: (
      <p>
        Columns can be added, removed, and rearranged whenever data are presented in a table. You can use table columns
        for filtering and sorting by clicking in the header.
      </p>
    ),
    target: "#table-page-column-tools",
  },
  {
    title: "Save “Groups” and “Views”",
    content: (
      <p>
        The Save button allows you to save content to your {getProjectLabel(false, true)}. You can choose to save lists
        of items (groups) or save the entire page with its current configuration (views).
      </p>
    ),
    target: "#table-page-save-button",
  },
];

export const getTipsAreOffTip = (appName: string) => ({
  title: "Getting Started tips are off",
  content: (
    <>
      <p>{appName} will no longer guide you through the features in the application.</p>
      <p>You can re-enable these tips at any time by selecting “Show Getting Started Tips” here in the help menu.</p>
    </>
  ),
  target: `#${GETTING_STARTED_ELEMENT_ID.HELP_MENU_BUTTON}`,
});

/**********  PLACEMENT UTILS COPIED AND ADAPTED FROM REACT_JOYRIDE  **************/

/* Copied/adapted from Joyride utils. */
export function calcPlacement(step: ITip) {
  const target = document.querySelector(step.target as string);

  /* istanbul ignore else */
  if (!target) {
    return;
  }

  const placement = {
    x: -1000,
    y: -1000,
  };

  /* istanbul ignore else */
  if (step) {
    const offsetX = path(["style", "beacon", "offsetX"], step)! || 0;
    const offsetY = path(["style", "beacon", "offsetY"], step)! || 0;
    const position = calcPosition(step);
    const body = document.body.getBoundingClientRect();
    const scrollTop = step.isFixed === true ? 0 : body.top;
    const component = getElementDimensions();
    const rect = target.getBoundingClientRect();

    // Calculate x position
    if (position.startsWith("left")) {
      placement.x = rect.left - component.width / 2 + offsetX;
    } else if (position.startsWith("right")) {
      placement.x = rect.left + rect.width - (component.width / 2 - offsetX);
    } else {
      placement.x = rect.left + (rect.width / 2 - component.width / 2);
    }

    // Calculate y position
    if (position.startsWith("top")) {
      placement.y = rect.top - scrollTop - (component.height / 2 + offsetY);
    } else if (position.startsWith("bottom")) {
      placement.y = rect.top - scrollTop + (rect.height - (component.height / 2 - offsetY));
    } else {
      placement.y = rect.top - scrollTop;
    }

    /* istanbul ignore else */
    if (/^bottom|^top/.test(position)) {
      if (position.includes("left")) {
        placement.x = rect.left - component.width / 2;
      } else if (position.includes("right")) {
        placement.x = rect.left + (rect.width - component.width / 2);
      }
    }

    return {
      xPos: placement.x,
      yPos: placement.y,
    };
  }
}

const DEFAULTS = {
  position: "top",
  minWidth: 290,
};

/**
 * Copied/adapted from Joyride utils.
 * Update position for overflowing elements.
 */
function calcPosition(step: ITip): string {
  const tooltipOffset = 30; // TODO this is the default !
  const body = document.body;
  const target = document.querySelector(step.target as string);
  const rect = target.getBoundingClientRect();
  const { height, width = DEFAULTS.minWidth } = getElementDimensions();
  let position = step.placementBeacon || DEFAULTS.position;

  if (position.startsWith("left") && rect.left - (width + tooltipOffset) < 0) {
    position = "top";
  } else if (
    position.startsWith("right") &&
    rect.left + rect.width + (width + tooltipOffset) > body.getBoundingClientRect().width
  ) {
    position = "bottom";
  }

  if (position.startsWith("top") && rect.top + body.scrollTop - (height + tooltipOffset) < 0) {
    position = "bottom";
  } else if (
    position.startsWith("bottom") &&
    rect.bottom + body.scrollTop + (height + tooltipOffset) > getDocHeight()
  ) {
    position = "top";
  }
  return position;
}

/**
 * Copied from Joyride utils.
 * Helper function to get the browser-normalized "document height"
 */
function getDocHeight(): number {
  const body = document.body;
  const html = document.documentElement;

  return Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
}

/**
 * Copied/adapted from Joyride utils.
 * Get an element actual dimensions with margin
 *
 * @private
 * @returns {{height: number, width: number}}
 */
function getElementDimensions() {
  const el = document.querySelector(".joyride-beacon"); // TODO select the one that is currently calculated !

  let height = 0;
  let width = 0;

  if (el) {
    const styles = window.getComputedStyle(el);

    height =
      el.clientHeight + Number.parseInt(styles.marginTop || "0", 10) + Number.parseInt(styles.marginBottom || "0", 10);
    width =
      el.clientWidth + Number.parseInt(styles.marginLeft || "0", 10) + Number.parseInt(styles.marginRight || "0", 10);
  }

  return {
    height,
    width,
  };
}
